import React from "react";

import { faRedo } from "@fortawesome/free-solid-svg-icons";
import { Grid } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { useDispatch, useSelector } from "react-redux";

import { requestScans } from "actions";

import { AgGridPreview } from "components/AgGrid/AgGridPreview";
import { CollapseTableWrapper } from "components/AgGrid/TableWrapper";
import { SlimButton } from "components/Button";
import { Action, ActionIcon } from "components/Button/Actions";
import { Button } from "components/Form";
import { Column, Row } from "components/Layout";
import WaitForSync from "components/LoadingSpinner/WaitForSync";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/MaterialDialog";

import { ApiModel } from "constants/loading";
import { TransferStatus } from "constants/nlis";
import { P2PLegConfig } from "constants/p2pLeg/containerConfig";

import {
  currentSaleSelector,
  getConsignments,
  getCurrentSaleyardPic,
  getIsFetchingNLISInformation,
  getSaleLots,
} from "selectors";

import { NLISFileGrid } from "containers/NLISFiles/NLISFileGrid";

import { useDebounceSelector, useMountEffect, useToggle } from "hooks";

interface ConfirmTransferProps {
  config: P2PLegConfig;
  toggleShowConfirm: () => void;
  saleyardPic: string | null;
}

const ConfirmTransfer = ({
  config,
  toggleShowConfirm,
  saleyardPic,
}: ConfirmTransferProps) => {
  const dispatch = useDispatch();

  const transferableScans = useSelector(config.transferableScansSelector);

  const handleTransfer = () => {
    dispatch(
      config.actions.bulkCreate(
        transferableScans.map(scanRow => scanRow.scan.EID),
      ),
    );
    toggleShowConfirm();
  };

  return (
    <Dialog open onClose={toggleShowConfirm} maxWidth="lg" fullWidth>
      <DialogTitle onClose={toggleShowConfirm}>
        {config.ui.dialogTitle(saleyardPic)}
      </DialogTitle>
      <DialogContent>
        <CollapseTableWrapper>
          <AgGridPreview
            rowData={transferableScans}
            columnDefs={config.ui.confirmationColumnDefs}
            tableName={config.agGrid.previewTable}
          />
        </CollapseTableWrapper>
      </DialogContent>
      <DialogActions>
        <Button onClick={toggleShowConfirm}>Cancel</Button>
        <Button
          data-tour="submit"
          onClick={handleTransfer}
          disabled={!saleyardPic}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const NLISP2PLegFileTab = React.memo(
  ({ config }: { config: P2PLegConfig }) => {
    const dispatch = useDispatch();

    const sale = useSelector(currentSaleSelector);
    const saleyardPic = useSelector(getCurrentSaleyardPic);
    const isFetching = useSelector(getIsFetchingNLISInformation);
    const rowData = useSelector(config.transactionsSelector);
    const [showConfirm, toggleShowConfirm] = useToggle(false);

    const handleRefresh = React.useCallback(() => {
      if (!isFetching) {
        dispatch(config.actions.fileAction.request());
        dispatch(config.actions.reversalAction.request());
        dispatch(requestScans(sale));
      }
    }, [isFetching, sale, dispatch, config]);

    useMountEffect(() => {
      if (rowData.length === 0) {
        handleRefresh();
      }
    });

    const transferableScans = useDebounceSelector(
      config.transferableScansSelector,
    );
    const consignments = useSelector(getConsignments);
    const saleLots = useSelector(getSaleLots);

    const hasUnsubmittedTransfers = rowData.some(
      nlisTransaction => nlisTransaction.status === TransferStatus.UNSUBMITTED,
    );
    const hasActionable = transferableScans.length > 0;
    const showAction = hasActionable && !hasUnsubmittedTransfers;

    const transferText = config.ui.bannerText({
      transferableScans,
      consignments,
      saleLots,
    });

    const AlertAction = (
      <SlimButton
        data-tour="transferNow"
        onClick={toggleShowConfirm}
        disabled={hasUnsubmittedTransfers || isFetching}
      >
        Transfer Now
      </SlimButton>
    );

    return (
      <WaitForSync
        requiredData={[
          config.apiModel.files,
          config.apiModel.reversals,
          ApiModel.SCANS,
        ]}
      >
        <Row flexGrow justifyBetween>
          <Column fullWidth padding={2}>
            {showAction && (
              <Grid item xs={12}>
                <Alert severity="error" action={AlertAction}>
                  {transferText}
                </Alert>
              </Grid>
            )}
            {!showAction && !hasUnsubmittedTransfers && (
              <Grid item xs={12}>
                <Alert severity="success">{config.ui.allDoneMessage}</Alert>
              </Grid>
            )}
            {hasUnsubmittedTransfers && (
              <Alert severity="warning">
                There are transfers pending submission. Check back later.
              </Alert>
            )}
          </Column>

          <Action onClick={handleRefresh} disabled={isFetching}>
            <ActionIcon icon={faRedo} />
            Refresh
          </Action>
        </Row>

        <Column fullHeight>
          <NLISFileGrid
            rowData={rowData}
            tableName={config.agGrid.table}
            columnDefs={config.agGrid.transactionColumnDefs}
          />
        </Column>

        {showConfirm && (
          <ConfirmTransfer
            config={config}
            toggleShowConfirm={toggleShowConfirm}
            saleyardPic={saleyardPic}
          />
        )}
      </WaitForSync>
    );
  },
);
